// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { clearApiCache, getUserInfo } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const AuthContext = createContext({
  user: null,
  session: null,
  userInfo: null,
  loading: true,
  isEmailVerified: false,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let subscription = null;

    const init = async () => {
      try {
        if (!supabase) {
          setSession(null);
          setUserInfo(null);
          return;
        }

        const { data } = await supabase.auth.getSession();
        setSession(data?.session ?? null);

        if (data?.session) {
          try {
            const fetchedUserInfo = await getUserInfo();
            setUserInfo(fetchedUserInfo);
          } catch (error) {
            if (error?.response?.status === 401) setUserInfo(null);
          }
        }

        const { data: listener } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
          setSession(nextSession ?? null);

          if (!nextSession) {
            setUserInfo(null);
            clearApiCache();
            return;
          }

          try {
            const fetchedUserInfo = await getUserInfo();
            setUserInfo(fetchedUserInfo);
          } catch (error) {
            if (error?.response?.status === 401) setUserInfo(null);
          }
        });
        subscription = listener?.subscription ?? null;
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => {
      try {
        subscription?.unsubscribe?.();
      } catch {
        // ignore
      }
    };
  }, []);

  const value = useMemo(() => {
    const rawUser = session?.user ?? null;
    // 旧実装(firebase)互換: 既存コンポーネントは user.uid を参照しているため、Supabase の user.id を uid として提供する
    const user = rawUser ? { ...rawUser, uid: rawUser.id } : null;
    const metadataEmailVerified = rawUser?.user_metadata?.email_verified;
    const isEmailVerified =
      metadataEmailVerified === undefined || metadataEmailVerified === null
        ? Boolean(rawUser?.email_confirmed_at)
        : Boolean(metadataEmailVerified);

    return {
      user,
      session,
      userInfo,
      loading,
      isEmailVerified,
    };
  }, [session, userInfo, loading]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingSpinner text="認証情報を確認しています..." /> : children}
    </AuthContext.Provider>
  );
}
